import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Image,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import {
  RefreshCw,
  Settings,
  HeartPulse,
  ChartBarIcon,
  Info,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { pipe, find } from 'remeda';

import { getVrTooltipLabel } from '@utils/deviceTooltips';
import { formatAppVersionForDisplay } from '@utils/mapPolarHeartRateEvent';
import {
  ConnectedDevicesProps,
  ConnectionBadgeProps,
  Device,
  DeviceInfoProps,
  DeviceConnectionStatus,
} from '@types';
import { useBaselineCalculation } from '../../../../hooks/useBaselineCalculation';
import { useAuthUser } from '@store/useAuthStore';
import { useHeartRateStore } from '@store/useHeartRateStore';
import { useUserDevices } from '../../../../hooks/useUserDevices';
import { useDeviceConnectionsStatus } from '../../../../hooks/useDeviceConnectionsStatus';
import {
  getSensorDeviceImageSrc,
  getVrDeviceImageSrc,
} from '../../../../utils/deviceImage';

// ============================================================================
// TYPES
// ============================================================================

interface CardHeaderProps {
  title: string;
  refetch: () => void;
  loading: boolean;
  onConfigureClick: () => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getBadgeColor = (status: DeviceConnectionStatus): string => {
  if (status === 'disconnected') return 'red';
  if (status === 'receiving_data') return 'teal';
  if (status === 'connected_no_data') return 'yellow';
  if (status === 'connected') return 'teal';
  return 'gray';
};

const isKnownConnectionStatus = (status: DeviceConnectionStatus): boolean => {
  return (
    status === 'disconnected' ||
    status === 'receiving_data' ||
    status === 'connected_no_data' ||
    status === 'connected'
  );
};

const getBadgeText = (
  status: DeviceConnectionStatus,
  t: (key: string, fallback?: string) => string
): string => {
  if (status === 'disconnected') return t('not_connected', 'Non Connesso');
  if (status === 'receiving_data') return t('connected', 'Connesso');
  if (status === 'connected_no_data')
    return t('connected_waiting', 'Connesso - In Attesa');
  if (status === 'connected') return 'Connesso';
  return t('unknown_status', 'Sconosciuto');
};

const matchesDeviceCode = (device: Device, deviceCode: string): boolean => {
  return (
    deviceCode === device.clientId ||
    deviceCode === device.code ||
    deviceCode === device.id?.toString()
  );
};

const matchesConnectedTo = (
  device: Device,
  connectedTo: string | number | undefined
): boolean => {
  if (!connectedTo) return false;
  return (
    connectedTo === device.clientId ||
    connectedTo === device.code ||
    connectedTo === device.id?.toString() ||
    (typeof connectedTo === 'string' &&
      !isNaN(Number(connectedTo)) &&
      Number(connectedTo) === device.id)
  );
};

const findDeviceBySessionCode = (
  allDevices: Device[],
  deviceCode: string | undefined,
  connectionStatus: Record<string, DeviceConnectionStatus>
): (Device & { status: DeviceConnectionStatus }) | undefined => {
  if (!deviceCode) return undefined;

  const device = pipe(
    allDevices,
    find(
      (d: Device) => matchesDeviceCode(d, deviceCode) && d.type === 'vr-headset'
    )
  );

  if (device) {
    const clientId = device.clientId ?? device.code;
    return {
      ...device,
      status:
        connectionStatus[clientId ?? ''] ?? device.status ?? 'disconnected',
    };
  }

  return undefined;
};

const findFirstVrHeadset = (
  allDevices: Device[],
  connectionStatus: Record<string, DeviceConnectionStatus>
): (Device & { status: DeviceConnectionStatus }) | undefined => {
  const device = pipe(
    allDevices,
    find((d: Device) => d.type === 'vr-headset')
  );

  if (!device) return undefined;

  const clientId = device.clientId ?? device.code;
  return {
    ...device,
    status: connectionStatus[clientId ?? ''] ?? device.status ?? 'disconnected',
  };
};

const findSensorBySessionConnectedTo = (
  allDevices: Device[],
  connectedTo: string | number | undefined,
  connectionStatus: Record<string, DeviceConnectionStatus>
): (Device & { status: DeviceConnectionStatus }) | undefined => {
  if (!connectedTo) return undefined;

  const sensorDevice = pipe(
    allDevices,
    find(
      (d: Device) =>
        matchesConnectedTo(d, connectedTo) && d.type === 'smartwatch'
    )
  );

  if (sensorDevice) {
    const clientId = sensorDevice.clientId ?? sensorDevice.code;
    return {
      ...sensorDevice,
      status:
        connectionStatus[clientId ?? ''] ??
        sensorDevice.status ??
        'disconnected',
    };
  }

  return undefined;
};

const findSensorById = (
  allDevices: Device[],
  sensorId: number | null | undefined,
  connectionStatus: Record<string, DeviceConnectionStatus>
): (Device & { status: DeviceConnectionStatus }) | undefined => {
  if (sensorId == null) return undefined;

  const sensorDevice = pipe(
    allDevices,
    find(
      (d: Device) =>
        d.type === 'smartwatch' &&
        (d.id === sensorId || String(d.id) === String(sensorId))
    )
  );

  if (!sensorDevice) return undefined;

  const clientId = sensorDevice.clientId ?? sensorDevice.code;
  return {
    ...sensorDevice,
    status:
      connectionStatus[clientId ?? ''] ??
      sensorDevice.status ??
      'disconnected',
  };
};

const findSensorByDeviceRelationship = (
  allDevices: Device[],
  deviceId: number | undefined,
  connectionStatus: Record<string, DeviceConnectionStatus>
): (Device & { status: DeviceConnectionStatus }) | undefined => {
  if (!deviceId) return undefined;

  const sensorDevice = pipe(
    allDevices,
    find(
      (d: Device) =>
        d.type === 'smartwatch' &&
        (d.connected_to === deviceId || d.connected_to === deviceId.toString())
    )
  );

  if (sensorDevice) {
    const clientId = sensorDevice.clientId ?? sensorDevice.code;
    return {
      ...sensorDevice,
      status:
        connectionStatus[clientId ?? ''] ??
        sensorDevice.status ??
        'disconnected',
    };
  }

  return undefined;
};

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

export const ConnectionBadge = ({
  status,
  tooltipLabel,
}: ConnectionBadgeProps) => {
  const { t } = useTranslation();
  if (!isKnownConnectionStatus(status)) return null;

  const isConnected = status === 'connected' || status === 'receiving_data';
  const translate = (key: string, fallback?: string) => {
    const result = t(key);
    return result === key && fallback ? fallback : result;
  };

  const badge = (
    <Badge
      color={getBadgeColor(status)}
      variant='light'
      radius='sm'
      leftSection={
        !isConnected ? <WifiOff size={16} /> : <Wifi size={16} />
      }
      size='sm'
      style={{ cursor: tooltipLabel ? 'help' : 'default' }}
    >
      {getBadgeText(status, translate)}
    </Badge>
  );

  if (tooltipLabel) {
    return (
      <Tooltip
        label={tooltipLabel}
        position='right'
        withArrow
        disabled={!tooltipLabel}
      >
        {badge}
      </Tooltip>
    );
  }

  return badge;
};

// ============================================================================
// VR DEVICE CARD
// ============================================================================

interface VrDeviceCardProps {
  imageSrc: string;
  imageAlt: string;
  deviceInfo: DeviceInfoProps[];
  tooltipLabel?: string;
  deviceStatus: DeviceConnectionStatus;
  isRequired?: boolean;
  children?: React.ReactNode;
}

export const VrDeviceCard = ({
  imageSrc,
  imageAlt,
  deviceInfo,
  tooltipLabel,
  deviceStatus,
  isRequired,
  children,
}: VrDeviceCardProps) => {
  const isActive =
    deviceStatus === 'connected' || deviceStatus === 'receiving_data';
  const isConnectedNoData = deviceStatus === 'connected_no_data';

  return (
    <Table>
      <Table.Tbody>
        <Table.Tr>
          <Table.Td style={{ width: 100, verticalAlign: 'middle' }}>
            <Image
              src={imageSrc}
              h={80}
              w={80}
              alt={imageAlt}
              className={isConnectedNoData ? 'blink-fade' : ''}
              style={{
                filter: isActive ? 'none' : 'grayscale(100%)',
                opacity: isActive ? 1 : 0.4,
              }}
            />
          </Table.Td>
          <Table.Td style={{ verticalAlign: 'middle' }}>
            <Group gap='xs' mb='xs'>
              <ConnectionBadge
                status={deviceStatus}
                tooltipLabel={tooltipLabel}
              />
              <Badge
                color={isRequired ? 'blue' : 'gray'}
                variant='light'
                size='sm'
              >
                {isRequired ? 'Obbligatorio' : 'Opzionale'}
              </Badge>
            </Group>
            {deviceInfo.map((info, index) => (
              <Text
                key={index}
                size='sm'
                mb={index < deviceInfo.length - 1 ? 4 : 0}
              >
                <Text component='span' c='dimmed' fw={500}>
                  {info.label}:
                </Text>{' '}
                <Text component='span' fw={600}>
                  {info.value}
                </Text>
              </Text>
            ))}
            {children}
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  );
};

// ============================================================================
// SENSOR DEVICE CARD
// ============================================================================

interface SensorDeviceCardProps {
  imageSrc: string;
  imageAlt: string;
  deviceInfo: DeviceInfoProps[];
  deviceStatus: DeviceConnectionStatus;
  isRequired?: boolean;
  baseline?: number;
  currentHeartRate?: number;
}

export const SensorDeviceCard = ({
  imageSrc,
  imageAlt,
  deviceInfo,
  deviceStatus,
  isRequired,
  baseline,
  currentHeartRate,
}: SensorDeviceCardProps) => {
  const theme = useMantineTheme();
  const isActive =
    deviceStatus === 'connected' || deviceStatus === 'receiving_data';
  const isConnectedNoData = deviceStatus === 'connected_no_data';
  const hasHeartRate = currentHeartRate !== undefined && currentHeartRate > 0;

  return (
    <Table>
      <Table.Tbody>
        <Table.Tr>
          <Table.Td style={{ width: 100, verticalAlign: 'middle' }}>
            <Image
              src={imageSrc}
              h={80}
              w={80}
              alt={imageAlt}
              className={isConnectedNoData ? 'blink-fade' : ''}
              style={{
                filter: isActive ? 'none' : 'grayscale(100%)',
                opacity: isActive ? 1 : 0.4,
              }}
            />
          </Table.Td>
          <Table.Td style={{ verticalAlign: 'middle' }}>
            <Group gap='xs' mb='xs'>
              <ConnectionBadge status={deviceStatus} />
              <Badge
                color={isRequired ? 'teal' : 'gray'}
                variant='light'
                size='sm'
              >
                {isRequired ? 'Obbligatorio' : 'Opzionale'}
              </Badge>
            </Group>
            {deviceInfo.map((info, index) => (
              <Text
                key={index}
                size='sm'
                mb={index < deviceInfo.length - 1 ? 4 : 0}
              >
                <Text component='span' c='dimmed' fw={500}>
                  {info.label}:
                </Text>{' '}
                <Text component='span' fw={600}>
                  {info.value}
                </Text>
              </Text>
            ))}
            <Divider my='xs' />
            <Group gap='md' mt='xs'>
              <Group gap={4}>
                <ChartBarIcon
                  size={14}
                  style={{ color: theme.colors.blue[6] }}
                />
                <Text size='sm' c='dimmed' fw={500}>
                  Baseline:
                </Text>
                <Text size='sm' fw={600} c='blue'>
                  {baseline ? `${baseline} bpm` : '--'}
                </Text>
              </Group>
              <Group gap={4}>
                <HeartPulse
                  size={14}
                  style={{
                    color: hasHeartRate
                      ? theme.colors.red[6]
                      : theme.colors.gray[6],
                    animation: hasHeartRate
                      ? 'pulse 1.5s ease-in-out infinite'
                      : 'none',
                  }}
                />
                <Text size='sm' c='dimmed' fw={500}>
                  Ritmo cardiaco:
                </Text>
                <Text
                  size='sm'
                  fw={600}
                  c={hasHeartRate ? 'red' : 'dimmed'}
                  style={{ fontFamily: 'monospace' }}
                >
                  {hasHeartRate ? `${currentHeartRate} bpm` : '--'}
                </Text>
              </Group>
            </Group>
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  );
};

const CardHeader = ({
  title,
  refetch,
  loading,
  onConfigureClick,
}: CardHeaderProps) => (
  <Card.Section withBorder inheritPadding py='xs'>
    <Group justify='space-between'>
      <Title order={4}>{title}</Title>
      <Group>
        <Button variant='default' size='xs' onClick={onConfigureClick}>
          <Settings size={16} />
        </Button>
        <Button variant='default' size='xs' onClick={refetch} loading={loading}>
          <RefreshCw size={14} />
        </Button>
      </Group>
    </Group>
  </Card.Section>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ConnectedDevices = ({
  onConfigureClick,
  vrRequired,
  sensorRequired,
  devices: devicesProp,
  session,
}: ConnectedDevicesProps) => {
  const { t } = useTranslation();
  const user = useAuthUser();
  const userId = user?.id?.toString();
  const currentSession = session;

  const {
    devices: allDevicesFromHook,
    error,
    refetch,
    isLoading,
  } = useUserDevices(userId);

  const allDevices =
    devicesProp && devicesProp.length > 0 ? devicesProp : allDevicesFromHook;

  const { connectionStatus } = useDeviceConnectionsStatus(allDevices);

  const devices = useMemo(() => {
    const deviceByCode = findDeviceBySessionCode(
      allDevices,
      currentSession?.deviceCode,
      connectionStatus
    );

    if (deviceByCode) {
      return deviceByCode;
    }

    return findFirstVrHeadset(allDevices, connectionStatus);
  }, [allDevices, currentSession?.deviceCode, connectionStatus]);

  const sensor = useMemo(() => {
    const sensorById = findSensorById(
      allDevices,
      currentSession?.sensor_id,
      connectionStatus
    );

    if (sensorById) {
      return sensorById;
    }

    const sensorByConnectedTo = findSensorBySessionConnectedTo(
      allDevices,
      currentSession?.connectedTo,
      connectionStatus
    );

    if (sensorByConnectedTo) {
      return sensorByConnectedTo;
    }

    return findSensorByDeviceRelationship(
      allDevices,
      devices?.id,
      connectionStatus
    );
  }, [
    allDevices,
    currentSession?.sensor_id,
    currentSession?.connectedTo,
    connectionStatus,
    devices?.id,
  ]);

  const sensorImageSrc = useMemo(() => {
    return getSensorDeviceImageSrc(sensor?.model);
  }, [sensor?.model]);

  const vrImageSrc = useMemo(() => {
    return getVrDeviceImageSrc(devices?.model);
  }, [devices?.model]);

  const isVrConnected = devices?.status || 'disconnected';
  const isSensorConnected = sensor?.status || 'disconnected';

  const { baseline } = useBaselineCalculation();
  const heartRateEvents = useHeartRateStore(s => s.events);
  const latestHeartRate = heartRateEvents.at(-1)?.hr;
  const currentHeartRate =
    latestHeartRate !== undefined && latestHeartRate > 0
      ? latestHeartRate
      : undefined;

  const sensorAppVersion = useMemo(() => {
    for (let i = heartRateEvents.length - 1; i >= 0; i -= 1) {
      const displayed = formatAppVersionForDisplay(
        heartRateEvents[i]?.appVersion
      );
      if (displayed) return displayed;
    }
    return null;
  }, [heartRateEvents]);

  return (
    <Card shadow='xs' withBorder>
      <CardHeader
        title='Dispositivi'
        onConfigureClick={onConfigureClick}
        refetch={() => refetch()}
        loading={isLoading}
      />
      <Card.Section inheritPadding py='md'>
        <Stack gap='0'>
          {!error && (
            <div>
              {!devices ? (
                <Text c='dimmed' size='sm' ta='center' py='md'>
                  Nessun dispositivo connesso
                </Text>
              ) : (
                <Stack gap='0'>
                  <VrDeviceCard
                    imageSrc={vrImageSrc}
                    imageAlt='VR Headset'
                    deviceInfo={[
                      {
                        label: t('vr_name', 'Nome visore'),
                        value: devices.name || devices.model,
                      },
                      {
                        label: t('vr_code', 'Visore ID'),
                        value: devices.code,
                      },
                    ]}
                    tooltipLabel={getVrTooltipLabel(
                      isVrConnected,
                      (key: string, fallback?: string) => {
                        const result = t(key);
                        return result === key && fallback ? fallback : result;
                      }
                    )}
                    isRequired={vrRequired}
                    deviceStatus={isVrConnected || 'disconnected'}
                  >
                    {isVrConnected === 'disconnected' && (
                      <>
                        <Divider mt={'xs'} />
                        <Box mt='xs'>
                          <Flex gap={5} align='flex-start'>
                            <Info size={24} color='#868e96' className='mr-2' />
                            <Text size='xs' c='gray.5'>
                              {t(
                                'vr_device_disclaimer',
                                `Per collegare il visore, accedi alla modalità "Webapp" dall${"'"}app. Lo stato diventa online entro un minuto.`
                              )}
                            </Text>
                          </Flex>
                        </Box>
                      </>
                    )}
                  </VrDeviceCard>
                  {sensor && (
                    <>
                      <Divider my='md' />
                      <SensorDeviceCard
                        imageSrc={sensorImageSrc}
                        imageAlt='Heart Rate Sensor'
                        isRequired={sensorRequired}
                        deviceStatus={isSensorConnected || 'disconnected'}
                        deviceInfo={[
                          {
                            label: t('sensor_name', 'Nome sensore'),
                            value: sensor?.name || sensor?.model,
                          },
                          {
                            label: t('sensor_code', 'Sensore ID'),
                            value: sensor?.code,
                          },
                          ...(sensorAppVersion
                            ? [
                                {
                                  label: t('sensor_app_version', 'Versione app'),
                                  value: sensorAppVersion,
                                },
                              ]
                            : []),
                        ]}
                        baseline={baseline}
                        currentHeartRate={currentHeartRate}
                      />
                    </>
                  )}
                </Stack>
              )}
            </div>
          )}
        </Stack>
      </Card.Section>
    </Card>
  );
};
